import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { chatAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, MessageSquare, Play } from 'lucide-react';
import { toast } from 'sonner';
import ReviewQuiz from '@/components/ReviewQuiz';

const ScenarioLesson = () => {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scenario, setScenario] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);

  const scenariosData = {
    restaurant: {
      id: 'restaurant',
      title: 'Restaurant',
      image: 'https://images.pexels.com/photos/29489315/pexels-photo-29489315.jpeg?auto=compress&cs=tinysrgb&w=800',
      introduction: {
        context: 'Learn how to order food, interact with waiters, and discuss menu items in a restaurant setting.',
        objectives: ['Order meals confidently', 'Ask about menu items', 'Request modifications', 'Handle the bill']
      },
      vocabulary: [
        { word: 'Menu', translation: 'Cardápio', usage: 'Can I see the menu, please?' },
        { word: 'Order', translation: 'Pedir', usage: "I'd like to order..." },
        { word: 'Waiter', translation: 'Garçom', usage: 'Excuse me, waiter!' },
        { word: 'Bill/Check', translation: 'Conta', usage: 'Can I have the bill, please?' },
        { word: 'Recommend', translation: 'Recomendar', usage: 'What do you recommend?' }
      ],
      exampleDialogue: [
        { role: 'waiter', text: 'Good evening! Are you ready to order?' },
        { role: 'you', text: 'Yes, what do you recommend?' },
        { role: 'waiter', text: 'Our chef special is the grilled salmon with vegetables.' },
        { role: 'you', text: "That sounds great! I'll have that, please." }
      ],
      guidedSteps: [
        'Greet the waiter and ask for the menu',
        'Ask about menu recommendations',
        'Place your order',
        'Ask about modifications (if needed)',
        'Request the bill'
      ]
    },
    job_interview: {
      id: 'job_interview',
      title: 'Job Interview',
      image: 'https://images.unsplash.com/photo-1637665662134-db459c1bbb46?w=800&h=500&fit=crop',
      introduction: {
        context: 'Master professional interview communication, from introducing yourself to answering common questions.',
        objectives: ['Introduce yourself professionally', 'Answer experience questions', 'Discuss strengths', 'Ask smart questions']
      },
      vocabulary: [
        { word: 'Experience', translation: 'Experiência', usage: 'I have 3 years of experience in...' },
        { word: 'Skills', translation: 'Habilidades', usage: 'My key skills include...' },
        { word: 'Background', translation: 'Formação', usage: 'My background is in...' },
        { word: 'Strength', translation: 'Ponto forte', usage: 'My greatest strength is...' },
        { word: 'Opportunity', translation: 'Oportunidade', usage: "I'm excited about this opportunity" }
      ],
      exampleDialogue: [
        { role: 'interviewer', text: 'Tell me about yourself.' },
        { role: 'you', text: 'I have 5 years of experience in software development, specializing in web applications.' },
        { role: 'interviewer', text: 'What are your key strengths?' },
        { role: 'you', text: 'I excel at problem-solving and working in team environments.' }
      ],
      guidedSteps: [
        'Introduce yourself professionally',
        'Describe your work experience',
        'Discuss your key strengths',
        'Explain why you want this position',
        'Ask questions about the role'
      ]
    },
    airport: {
      id: 'airport',
      title: 'Airport Check-in',
      image: 'https://images.unsplash.com/photo-1773734874748-8e7893de8e6f?w=800&h=500&fit=crop',
      introduction: {
        context: 'Navigate airport procedures with confidence - from check-in to boarding.',
        objectives: ['Check in smoothly', 'Handle luggage', 'Go through security', 'Find your gate']
      },
      vocabulary: [
        { word: 'Passport', translation: 'Passaporte', usage: "Here's my passport" },
        { word: 'Boarding pass', translation: 'Cartão de embarque', usage: 'Where can I print my boarding pass?' },
        { word: 'Luggage', translation: 'Bagagem', usage: 'How many bags can I check?' },
        { word: 'Gate', translation: 'Portão', usage: 'Which gate is my flight?' },
        { word: 'Delayed', translation: 'Atrasado', usage: 'Is the flight delayed?' }
      ],
      exampleDialogue: [
        { role: 'agent', text: 'Good morning! Passport and ticket, please.' },
        { role: 'you', text: 'Here you go. I have one bag to check.' },
        { role: 'agent', text: 'Perfect. Window or aisle seat?' },
        { role: 'you', text: 'Window, please.' }
      ],
      guidedSteps: [
        'Check in and show your documents',
        'Check your luggage',
        'Ask about your gate and boarding time',
        'Navigate to security',
        'Confirm your boarding'
      ]
    },
    shopping: {
      id: 'shopping',
      title: 'Shopping',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=500&fit=crop',
      introduction: {
        context: 'Master shopping conversations - from asking about sizes to negotiating prices.',
        objectives: ['Ask for sizes and colors', 'Try on clothes', 'Negotiate prices', 'Complete purchase']
      },
      vocabulary: [
        { word: 'Size', translation: 'Tamanho', usage: 'Do you have this in my size?' },
        { word: 'Fitting room', translation: 'Provador', usage: 'Where is the fitting room?' },
        { word: 'Price', translation: 'Preço', usage: 'How much is this?' },
        { word: 'Discount', translation: 'Desconto', usage: 'Do you have any discounts?' },
        { word: 'Receipt', translation: 'Recibo', usage: 'Can I have a receipt?' }
      ],
      exampleDialogue: [
        { role: 'assistant', text: 'Hello! Can I help you find something?' },
        { role: 'you', text: 'Yes, I\'m looking for a jacket.' },
        { role: 'assistant', text: 'What size do you need?' },
        { role: 'you', text: 'Medium, please. Can I try it on?' }
      ],
      guidedSteps: [
        'Greet the shop assistant',
        'Ask about a specific item',
        'Request your size or color',
        'Try on the item',
        'Ask about the price and pay'
      ]
    },
    doctor: {
      id: 'doctor',
      title: 'Doctor Appointment',
      image: 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=800&h=500&fit=crop',
      introduction: {
        context: 'Learn to describe symptoms and understand medical advice in a doctor\'s office.',
        objectives: ['Describe symptoms clearly', 'Answer medical questions', 'Understand diagnosis', 'Ask about treatment']
      },
      vocabulary: [
        { word: 'Symptom', translation: 'Sintoma', usage: 'I have flu symptoms.' },
        { word: 'Pain', translation: 'Dor', usage: 'I feel pain in my back.' },
        { word: 'Prescription', translation: 'Receita', usage: 'Do I need a prescription?' },
        { word: 'Allergy', translation: 'Alergia', usage: 'I have no allergies.' },
        { word: 'Appointment', translation: 'Consulta', usage: 'I have an appointment at 3 PM.' }
      ],
      exampleDialogue: [
        { role: 'doctor', text: 'What brings you in today?' },
        { role: 'you', text: 'I\'ve had a headache for two days.' },
        { role: 'doctor', text: 'Any other symptoms?' },
        { role: 'you', text: 'Yes, I feel tired and a bit dizzy.' }
      ],
      guidedSteps: [
        'Explain your main symptom',
        'Describe when symptoms started',
        'Answer doctor\'s questions',
        'Understand the diagnosis',
        'Ask about treatment or medication'
      ]
    },
    hotel: {
      id: 'hotel',
      title: 'Hotel Check-in',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=500&fit=crop',
      introduction: {
        context: 'Navigate hotel check-in and requests with confidence.',
        objectives: ['Check in smoothly', 'Ask about amenities', 'Request services', 'Handle room issues']
      },
      vocabulary: [
        { word: 'Reservation', translation: 'Reserva', usage: 'I have a reservation.' },
        { word: 'Room key', translation: 'Chave do quarto', usage: 'Here\'s your room key.' },
        { word: 'Amenities', translation: 'Comodidades', usage: 'What amenities do you have?' },
        { word: 'Wi-Fi', translation: 'Wi-Fi', usage: 'What\'s the Wi-Fi password?' },
        { word: 'Checkout', translation: 'Check-out', usage: 'What time is checkout?' }
      ],
      exampleDialogue: [
        { role: 'receptionist', text: 'Good evening! Do you have a reservation?' },
        { role: 'you', text: 'Yes, under the name Smith.' },
        { role: 'receptionist', text: 'Perfect! I have you here for 3 nights.' },
        { role: 'you', text: 'Great! What time is breakfast?' }
      ],
      guidedSteps: [
        'Provide your reservation details',
        'Receive room key and information',
        'Ask about hotel amenities',
        'Request any special services',
        'Confirm checkout time'
      ]
    }
  };

  useEffect(() => {
    const scenarioData = scenariosData[scenarioId];
    if (scenarioData) {
      setScenario(scenarioData);
    } else {
      // Fallback after timeout
      setTimeout(() => {
        if (!scenario) {
          toast.error('Scenario not found');
          navigate('/scenarios');
        }
      }, 3000);
    }
  }, [scenarioId]);

  const startPractice = async () => {
    try {
      const conversation = await chatAPI.createConversation('scenario', scenarioId);
      navigate(`/chat/${conversation.id}?guided=true`);
    } catch (error) {
      toast.error('Failed to start practice');
    }
  };

  const handleQuizComplete = () => {
    startPractice();
  };

  const skipToQuiz = () => {
    setShowQuiz(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!scenario) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="animate-pulse text-forest text-lg">Loading lesson...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      <header className="bg-white border-b border-forest/10 p-4">
        <div className="container mx-auto flex items-center gap-4">
          <Button onClick={() => navigate('/scenarios')} variant="ghost">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-fraunces text-2xl font-semibold text-forest">
            {scenario.title}
          </h1>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-12 max-w-4xl">
        {showQuiz ? (
          <div>
            <Button 
              onClick={() => setShowQuiz(false)} 
              variant="ghost" 
              className="mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Lesson
            </Button>
            <ReviewQuiz 
              vocabulary={scenario.vocabulary} 
              onComplete={handleQuizComplete}
            />
          </div>
        ) : (
          <>
            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-3xl overflow-hidden shadow-medium mb-12"
            >
              <img 
                src={scenario.image} 
                alt={scenario.title}
                className="w-full h-64 object-cover"
              />
            </motion.div>

        {/* Introduction */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl p-8 shadow-soft mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-6 h-6 text-forest" />
            <h2 className="font-fraunces text-2xl font-semibold text-forest">Introduction</h2>
          </div>
          <p className="text-forest mb-4">{scenario.introduction.context}</p>
          <h3 className="font-medium text-forest mb-2">What you'll learn:</h3>
          <ul className="space-y-2">
            {scenario.introduction.objectives.map((obj, i) => (
              <li key={i} className="flex items-center gap-2 text-forest">
                <div className="w-2 h-2 bg-lime rounded-full" />
                {obj}
              </li>
            ))}
          </ul>
        </motion.section>

        {/* Vocabulary */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl p-8 shadow-soft mb-8"
        >
          <h2 className="font-fraunces text-2xl font-semibold text-forest mb-6">Key Vocabulary</h2>
          <div className="space-y-4">
            {scenario.vocabulary.map((item, i) => (
              <div key={i} className="p-4 bg-sand rounded-2xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-forest text-lg">{item.word}</span>
                  <span className="text-stone">{item.translation}</span>
                </div>
                <p className="text-sm text-forest/70 italic">"{item.usage}"</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Example Dialogue */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-3xl p-8 shadow-soft mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare className="w-6 h-6 text-forest" />
            <h2 className="font-fraunces text-2xl font-semibold text-forest">Example Conversation</h2>
          </div>
          <div className="space-y-3">
            {scenario.exampleDialogue.map((line, i) => (
              <div 
                key={i}
                className={`p-4 rounded-2xl ${
                  line.role === 'you' 
                    ? 'bg-forest text-sand ml-8' 
                    : 'bg-sand text-forest mr-8'
                }`}
              >
                <span className="text-xs uppercase opacity-70 mb-1 block">
                  {line.role === 'you' ? 'You' : line.role}
                </span>
                <p>{line.text}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Guided Practice Steps */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-3xl p-8 shadow-soft mb-8"
        >
          <h2 className="font-fraunces text-2xl font-semibold text-forest mb-6">Practice Steps</h2>
          <div className="space-y-3">
            {scenario.guidedSteps.map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-lime rounded-full flex items-center justify-center font-bold text-forest">
                  {i + 1}
                </div>
                <p className="text-forest pt-1">{step}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Start Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center space-y-4"
        >
          <Button
            onClick={skipToQuiz}
            data-testid="start-quiz-btn"
            className="bg-lime hover:bg-lime/90 text-forest rounded-full px-12 py-6 text-lg font-medium shadow-soft hover:shadow-medium transition-all"
          >
            <BookOpen className="w-5 h-5 mr-2" />
            Take Review Quiz
          </Button>
          <div>
            <Button
              onClick={startPractice}
              data-testid="skip-to-practice-btn"
              variant="ghost"
              className="text-forest hover:text-forest/80"
            >
              Skip quiz and start practice →
            </Button>
          </div>
        </motion.div>
          </>
        )}
      </div>
    </div>
  );
};

export default ScenarioLesson;
